import { useCallback, useEffect, useRef, useState } from "react"
import { X, Mail, Pencil, LogOut } from "lucide-react"
import { useAuth, getStoredToken } from "../hooks/useAuth"
import { useI18n } from "../i18n"
import type { LocalPreferences } from "../hooks/useSettings"
import type { ModelEntry } from "../shared/chat-config"
import { DEFAULT_PERSONA_NAME, PERSONA_SUBTITLE, PERSONA_SHORT_DESC } from "../brain/persona"
import { VOICE_GROUPS } from "../voice/voices"

// ─── Google Accounts types ────────────────────────────────────────────────────

interface GoogleAccount {
  account_slot: string
  account_email: string
  display_name: string | null
  picture_url: string | null
  status: string
  last_error: string | null
  scopes: string[]
  connected_at: number
}

// Scopes added in Task #3 (Docs/Sheets sprint). Connected slots that pre-date
// this change will be missing these scopes and need a passive re-consent prompt.
const DOCS_SHEETS_SCOPES = [
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive.file',
] as const

function isMissingDocsScopes(account: GoogleAccount | undefined): boolean {
  if (!account) return false
  return DOCS_SHEETS_SCOPES.some((s) => !account.scopes.includes(s))
}

interface SettingsPanelProps {
  open: boolean
  onClose: () => void
  onRestartVoice?: () => void
  prefs: LocalPreferences
  updatePref: <K extends keyof LocalPreferences>(key: K, value: LocalPreferences[K]) => void
  sendReasoningConfig?: (enabled: boolean, readReasoningAloud?: boolean) => void
  showReasoning?: boolean
  /** App-owned live registry and model state. These must be the same values used
   * by the input-bar picker so every model surface reaches the chat WebSocket. */
  availableModels: ModelEntry[]
  currentModel: ModelEntry
  onModelChange: (modelId: string) => void
  /** PATH B SLICE 4 — server-sourced companion display fields (CompanionDescriptor).
   *  Fall back to persona.ts's build-time constants when absent (cold render). */
  companionName?: string
  companionSubtitle?: string
  companionShortDesc?: string
}

// Google and Discord SVG icons — inline to avoid extra deps
function GoogleIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

function DiscordIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.032.055a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  )
}


function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      className={`settings-toggle ${checked ? "on" : ""}`}
      onClick={onChange}
    />
  )
}

export function SettingsPanel({
  open, onClose, onRestartVoice, prefs, updatePref, sendReasoningConfig, showReasoning,
  availableModels, currentModel, onModelChange,
  companionName = DEFAULT_PERSONA_NAME,
  companionSubtitle = PERSONA_SUBTITLE,
  companionShortDesc = PERSONA_SHORT_DESC,
}: SettingsPanelProps) {
  const { user, logout, refetch } = useAuth()
  const { t, locale, setLocale } = useI18n()
  const panelRef = useRef<HTMLDivElement>(null)

  // Display name inline edit state
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState("")
  const [nameSaved, setNameSaved] = useState(false)
  const [nameSaving, setNameSaving] = useState(false)

  // Google Accounts state
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccount[]>([])
  const [googleConnecting, setGoogleConnecting] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)

  // Sync name input when user data loads or panel opens
  useEffect(() => {
    if (open) {
      setNameValue(user?.display_name ?? "")
      setEditingName(false)
      setNameSaved(false)
    }
  }, [open, user?.display_name])

  // Escape key closes the panel
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, onClose])

  // Fetch Google account status when panel opens
  const fetchGoogleAccounts = useCallback(async () => {
    try {
      const authToken = getStoredToken()
      if (!authToken) return
      const res = await fetch("/api/google/accounts", {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) return
      const data = await res.json() as { accounts: GoogleAccount[] }
      setGoogleAccounts(data.accounts)
    } catch {
      // Non-fatal — OAuth section simply shows no accounts
    }
  }, [])

  useEffect(() => {
    if (open) {
      void fetchGoogleAccounts()
    }
  }, [open, fetchGoogleAccounts])

  async function handleConnectGoogle(slot: 'personal' | 'funkatorium' | 'legal') {
    setGoogleConnecting(true)
    setGoogleError(null)
    try {
      const authToken = getStoredToken()
      if (!authToken) {
        setGoogleError("Not authenticated")
        return
      }
      const res = await fetch(`/api/auth/google/start?account_slot=${slot}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        const err = await res.json() as { message?: string }
        setGoogleError(err.message ?? `Server error: ${res.status}`)
        return
      }
      const { authorize_url } = await res.json() as { authorize_url: string }

      // A.1 SECURITY: noopener is intentionally omitted here. The popup is our own callback
      // page (same origin) and must be able to call window.opener.postMessage() to signal
      // success back to this panel. noopener would null window.opener in the popup, silently
      // breaking the postMessage delivery.
      const popup = window.open(authorize_url, "_blank", "width=500,height=700")

      // Listen for postMessage from the popup callback page
      function handleMessage(event: MessageEvent) {
        // Only accept messages from same origin
        if (event.origin !== window.location.origin) return
        if (typeof event.data !== "object" || event.data.type !== "google_oauth_success") return

        window.removeEventListener("message", handleMessage)
        // Refresh the accounts list and clear connecting state
        void fetchGoogleAccounts()
        setGoogleConnecting(false)
      }

      window.addEventListener("message", handleMessage)

      // D.4: If popup closes without the postMessage firing, tell the user what happened.
      // H.1: setGoogleConnecting(false) lives only here (popup-closed path) and in the
      //      handleMessage success path above — NOT in the finally block, which would
      //      prematurely clear the connecting state before the popup interaction completes.
      const pollClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollClosed)
          window.removeEventListener("message", handleMessage)
          setGoogleError("Authentication window closed without completing. Try again.")
          setGoogleConnecting(false)
        }
      }, 500)

    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : "Connection failed")
      setGoogleConnecting(false)
    }
    // Note: setGoogleConnecting(false) is NOT in a finally block. The connecting state
    // must stay true while the popup is open. It clears when:
    //   (a) postMessage success fires (handleMessage above), or
    //   (b) popup closes without completing (pollClosed above), or
    //   (c) an error occurs fetching the authorize URL (catch block above).
  }

  if (!open) return null

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  function startEditName() {
    setNameValue(user?.display_name ?? "")
    setEditingName(true)
    setNameSaved(false)
  }

  async function saveName() {
    if (nameSaving) return
    const trimmed = nameValue.trim()
    // No change — just exit edit mode
    if (trimmed === (user?.display_name ?? "")) {
      setEditingName(false)
      return
    }
    setNameSaving(true)
    try {
      const res = await fetch("/api/auth/display-name", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: trimmed }),
      })
      if (res.ok) {
        setNameSaved(true)
        setEditingName(false)
        await refetch()
        setTimeout(() => setNameSaved(false), 2000)
      }
    } finally {
      setNameSaving(false)
    }
  }

  function handleNameKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") saveName()
    if (e.key === "Escape") setEditingName(false)
  }

  function handleLogout() {
    if (window.confirm(t("settings.account.logoutConfirm"))) {
      logout()
    }
  }

  return (
    <div className="settings-overlay" onClick={handleOverlayClick}>
      <div className="settings-panel open" ref={panelRef}>
        {/* Header */}
        <div className="settings-header">
          <span className="display">{t("settings.title")}</span>
          <button
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            aria-label="Close settings"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="settings-scroll">
          {/* COMPANION section */}
          <div className="settings-section">
            <div className="settings-section-label">{t("settings.companion")}</div>

            <div className="companion-card">
              <div className="companion-card-name">{companionName}</div>
              <div className="companion-card-subtitle">{companionSubtitle}</div>
              <div className="companion-card-desc">{companionShortDesc}</div>
            </div>

          </div>

          {/* ACCOUNT section */}
          <div className="settings-section">
            <div className="settings-section-label">{t("settings.account")}</div>

            {/* Email */}
            <div className="settings-row">
              <div>
                <div className="settings-row-label">{t("settings.account.email")}</div>
              </div>
              <div className="settings-row-value" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Mail size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
                <span>{user?.email ?? ""}</span>
              </div>
            </div>

            {/* Display Name */}
            <div className="settings-row">
              <div className="settings-row-label">{t("settings.account.displayName")}</div>
              <div>
                {editingName ? (
                  <input
                    className="settings-inline-input"
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onBlur={saveName}
                    onKeyDown={handleNameKeyDown}
                    maxLength={64}
                    autoFocus
                    disabled={nameSaving}
                  />
                ) : (
                  <button
                    className="settings-inline-edit btn-ghost"
                    onClick={startEditName}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    <span className="settings-row-value">
                      {nameSaved
                        ? t("settings.account.nameSaved")
                        : (user?.display_name ?? "—")}
                    </span>
                    <Pencil size={13} style={{ opacity: 0.5, marginLeft: 6 }} />
                  </button>
                )}
              </div>
            </div>


            {/* Linked Accounts */}
            <div className="settings-row">
              <div className="settings-row-label">{t("settings.account.linkedAccounts")}</div>
              <div className="settings-linked-accounts">
                <span
                  className={`settings-linked-icon ${user?.google_linked ? "connected" : "disconnected"}`}
                  title={user?.google_linked ? "Google connected" : "Google not connected"}
                >
                  <GoogleIcon size={15} />
                </span>
                <span
                  className={`settings-linked-icon ${user?.discord_linked ? "connected" : "disconnected"}`}
                  title={user?.discord_linked ? "Discord connected" : "Discord not connected"}
                >
                  <DiscordIcon size={15} />
                </span>
              </div>
            </div>

            {/* Logout */}
            <div className="settings-row" style={{ marginTop: "var(--space-2)" }}>
              <button
                className="btn btn-ghost"
                onClick={handleLogout}
                style={{ width: "100%", justifyContent: "center", color: "var(--text-muted)" }}
              >
                <LogOut size={15} style={{ marginRight: 6 }} />
                {t("settings.account.logout")}
              </button>
            </div>
          </div>

          {/* GOOGLE ACCOUNTS section */}
          <div className="settings-section">
            <div className="settings-section-label">Google Accounts</div>

            {/* Passive re-consent banner: shown when any connected slot is missing
                the Docs/Sheets/Drive scopes added in the Task #3 sprint. Surfaces
                per-slot — never force-prompts all slots simultaneously. */}
            {(["personal", "funkatorium", "legal"] as const).some((slot) =>
              isMissingDocsScopes(googleAccounts.find(a => a.account_slot === slot))
            ) && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  background: "var(--color-warning-bg, rgba(217, 119, 6, 0.08))",
                  border: "1px solid var(--color-warning-border, rgba(217, 119, 6, 0.3))",
                  borderRadius: "var(--radius-sm, 6px)",
                  padding: "var(--space-2) var(--space-3)",
                  marginBottom: "var(--space-2)",
                  fontSize: "var(--text-xs)",
                  color: "var(--color-warning, #d97706)",
                }}
              >
                <strong>Docs &amp; Sheets tools need updated permissions.</strong>
                {" "}Reconnect the accounts below to authorize Google Docs, Sheets, and Drive access.
              </div>
            )}

            {/* Render a slot row with status-aware UI. Four states:
                  - connected + full scopes:    green hint, no button
                  - connected + missing scopes: amber hint, Reconnect for Docs/Sheets button
                  - needs_reauth:               amber hint + last_error, Reconnect button
                  - absent:                     grey hint, Connect button */}
            {(["personal", "funkatorium", "legal"] as const).map((slot) => {
              const account = googleAccounts.find(a => a.account_slot === slot)
              const label = slot === "personal" ? "Personal" : slot === "funkatorium" ? "Funkatorium" : "Legal"
              const isConnected = account?.status === "connected"
              const needsReauth = account != null && !isConnected
              const missingScopes = isConnected && isMissingDocsScopes(account)
              // Show reconnect button when: not connected, needs_reauth, or missing Docs/Sheets scopes
              const showReconnect = !isConnected || missingScopes

              return (
                <div key={slot} className="settings-row">
                  <div>
                    <div className="settings-row-label">{label}</div>
                    {/* D.3: aria-live so screen readers hear the post-OAuth status update */}
                    <div aria-live="polite" aria-atomic="true">
                      {isConnected && !missingScopes && (
                        <div className="settings-row-hint" style={{ color: "var(--color-success, #22863a)" }}>
                          Connected as {account.account_email}
                        </div>
                      )}
                      {isConnected && missingScopes && (
                        <div className="settings-row-hint" style={{ color: "var(--color-warning, #d97706)" }}>
                          Connected as {account.account_email} — Docs/Sheets permissions missing
                        </div>
                      )}
                      {needsReauth && (
                        <div className="settings-row-hint" style={{ color: "var(--color-warning, #d97706)" }}>
                          Reconnect needed
                          {account.last_error && ` — ${account.last_error}`}
                        </div>
                      )}
                      {!account && (
                        <div className="settings-row-hint">Not connected</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "var(--space-1)" }}>
                    {showReconnect && (
                      <button
                        className="btn btn-ghost"
                        onClick={() => void handleConnectGoogle(slot)}
                        disabled={googleConnecting}
                        aria-disabled={googleConnecting}
                        aria-label={googleConnecting
                          ? "Connecting to Google, please wait"
                          : missingScopes
                          ? `Reconnect ${label} to enable Docs and Sheets tools (opens new window)`
                          : needsReauth
                          ? `Reconnect ${label} Google account (opens new window)`
                          : `Connect ${label} Google account (opens new window)`}
                        style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "var(--text-xs)" }}
                      >
                        <GoogleIcon size={14} />
                        {googleConnecting
                          ? "Connecting..."
                          : missingScopes
                          ? "Reconnect for Docs/Sheets"
                          : needsReauth
                          ? "Reconnect"
                          : "Connect Google"}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}

            {/* D.1: role="alert" gives implicit aria-live="assertive" so errors announce immediately */}
            {googleError && (
              <div role="alert">
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-error, #c0392b)", marginTop: "var(--space-1)" }}>
                  {googleError}
                </div>
              </div>
            )}
          </div>

          {/* APPEARANCE section */}
          <div className="settings-section">
            <div className="settings-section-label">{t("settings.appearance")}</div>

            {/* Language */}
            <div className="settings-row">
              <div className="settings-row-label">{t("settings.appearance.language")}</div>
              <div className="settings-segmented">
                <button
                  className={locale === "en" ? "active" : ""}
                  aria-pressed={locale === "en"}
                  onClick={() => setLocale("en")}
                >
                  EN
                </button>
                <button
                  className={locale === "de" ? "active" : ""}
                  aria-pressed={locale === "de"}
                  onClick={() => setLocale("de")}
                >
                  DE
                </button>
              </div>
            </div>

          </div>

          {/* CHAT section */}
          <div className="settings-section">
            <div className="settings-section-label">{t("settings.chat")}</div>

            {/* Default Model — RC3: shows and edits the per-companion model (same as
                the chat bar picker). user.default_model is no longer the source of
                truth; per-identity preferences on the gateway are authoritative.
                Changing this calls App's shared model handler, which triggers a config_change to the
                gateway — the pick is persisted server-side in identity_preferences. */}
            <div className="settings-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
              <div className="settings-row-label">{t("settings.chat.defaultModel")}</div>
              <select
                className="settings-select"
                aria-label={t("settings.chat.defaultModel")}
                value={currentModel.id}
                onChange={(e) => {
                  if (e.target.value) onModelChange(e.target.value)
                }}
              >
                {availableModels.map((m) => (
                  <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>
                ))}
              </select>
            </div>

            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: "var(--space-2)" }}>
              {t("settings.chat.defaultsNote")}
            </div>

            {/* Show reasoning toggle */}
            <div className="settings-row">
              <div>
                <div className="settings-row-label">Show reasoning</div>
                <div className="settings-row-hint">Ask the companion to think out loud before replying</div>
              </div>
              <ToggleSwitch
                checked={showReasoning ?? prefs.showReasoning}
                onChange={() => {
                  const next = !(showReasoning ?? prefs.showReasoning);
                  updatePref("showReasoning", next);
                  sendReasoningConfig?.(next, prefs.readReasoningAloud);
                }}
              />
            </div>

            {/* Read reasoning aloud — only visible when showReasoning is ON */}
            {(showReasoning ?? prefs.showReasoning) && (
              <div className="settings-row settings-row--sub">
                <div>
                  <div className="settings-row-label">Read reasoning aloud</div>
                  <div className="settings-row-hint">Include thinking in auto-speak (for auditory processing)</div>
                </div>
                <ToggleSwitch
                  checked={prefs.readReasoningAloud}
                  onChange={() => {
                    const next = !prefs.readReasoningAloud;
                    updatePref("readReasoningAloud", next);
                    sendReasoningConfig?.(showReasoning ?? prefs.showReasoning ?? true, next);
                  }}
                />
              </div>
            )}
          </div>

          {/* VOICE section */}
          <div className="settings-section">
            <div className="settings-section-label">{t("settings.voice")}</div>

            {/* Voice picker */}
            <div className="settings-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
              <div className="settings-row-label">{t("settings.voice.voice")}</div>
              <select
                className="settings-select"
                value={prefs.voiceId}
                onChange={(e) => updatePref("voiceId", e.target.value)}
              >
                {VOICE_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.voices.map(v => (
                      <option key={v.id} value={v.id}>{v.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Auto-speak toggle */}
            <div className="settings-row">
              <div>
                <div className="settings-row-label">{t("settings.voice.autoSpeak")}</div>
                <div className="settings-row-hint">{t("settings.voice.autoSpeakHint")}</div>
              </div>
              <ToggleSwitch
                checked={prefs.autoSpeak}
                onChange={() => updatePref("autoSpeak", !prefs.autoSpeak)}
              />
            </div>

            {/* Playback speed */}
            <div className="settings-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
              <div>
                <div className="settings-row-label">{t("settings.voice.speed")}</div>
                <div className="settings-row-hint">{t("settings.voice.speedHint")}</div>
              </div>
              <div className="settings-segmented">
                {[0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map((s) => (
                  <button
                    key={s}
                    className={prefs.playbackSpeed === s ? "active" : ""}
                    aria-pressed={prefs.playbackSpeed === s}
                    onClick={() => updatePref("playbackSpeed", s)}
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>

            {/* TTS engine selector */}
            <div className="settings-row">
              <div>
                <div className="settings-row-label">{t("settings.voice.ttsEngine")}</div>
                <div className="settings-row-hint">{t("settings.voice.ttsEngineHint")}</div>
              </div>
              <div className="settings-segmented">
                <button
                  className={prefs.ttsMode === "server" ? "active" : ""}
                  aria-pressed={prefs.ttsMode === "server"}
                  onClick={() => updatePref("ttsMode", "server")}
                >
                  {t("settings.voice.ttsServer")}
                </button>
                <button
                  className={prefs.ttsMode === "browser" ? "active" : ""}
                  aria-pressed={prefs.ttsMode === "browser"}
                  onClick={() => updatePref("ttsMode", "browser")}
                >
                  {t("settings.voice.ttsBrowser")}
                </button>
              </div>
            </div>

            {/* Restart voice engine */}
            <div className="settings-row">
              <div>
                <div className="settings-row-label">{t("settings.voice.engine")}</div>
                <div className="settings-row-hint">{t("settings.voice.engineHint")}</div>
              </div>
              <button
                className="btn btn-ghost"
                onClick={onRestartVoice}
                style={{ fontSize: "var(--text-xs)" }}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
